import {
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CmpsScorecardSubHeaderData,
    CmpsScoreSectionData,
    CmpsScoreSectionMenuData,
    CmpsScoreSectionTrendData,
} from '@cmps/ng-kit';
import { animationFrameScheduler, of } from 'rxjs';
import {
    auditTime,
    catchError,
    delay,
    retryWhen,
    scan,
    take,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { takeUntilDestroy } from '../common/take-until-destroy.decorator';
import { actions, addPageAction } from '../scorecard/new-relic';
import { datasetKey } from '../scorecard/scorecard.model';
import { StateToProps } from '../store';
import { useDispatcher, UseStore } from '../store.decorator';

import { HistoryPositionNavigate } from './general/history-position-navigate.model';
import {
    clearState,
    listData,
    navigateHistory,
    reload,
} from './my-scorecard.action';
import { TranslationKeyMap } from './my-scorecard.i18n';
import { MenuItems } from './my-scorecard.model';
import { scorecardCardReducer } from './my-scorecard.reducer';
import { ScorecardState } from './my-scorecard.state';

@Component({
    selector: '[cmpsMyScorecard]',
    templateUrl: './my-scorecard.component.html',
})
@UseStore<ScorecardState>('scorecard.card', scorecardCardReducer)
export class MyScorecardComponent
    implements OnInit, OnDestroy, StateToProps<ScorecardState>
{
    state: ScorecardState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _isExpanded: boolean = false;
    /** @private */ _trendMode: boolean = false;
    /** @private */ adjustScoreData: CmpsScoreSectionData;
    /** @private */ originalScoreData: CmpsScoreSectionData;
    /** @private */ trendData: CmpsScoreSectionTrendData;
    /** @private */ dropdownMenuData: CmpsScoreSectionMenuData;
    /** @private */ periodOptions: CmpsScorecardSubHeaderData[];
    /** @private */ _menuItems = MenuItems;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _authService: AuthService,
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        useDispatcher(this, reload({ user: this._authService.user }));
    }

    ngOnDestroy(): void {
        useDispatcher(this, clearState({}));
    }

    /** @private */
    _onReload(): void {
        addPageAction(actions.MyScorecardCard.RELOAD);
        useDispatcher(this, reload({ user: this._authService.user }));
    }

    /** @private */
    _onPeriodTypeChange(periodName: string): void {
        addPageAction(actions.MyScorecardCard.PERIOD_TYPE_CHANGE, {
            periodName,
        });
        const type = this.state.periodTypes.find(
            (e) => e.periodTypeName === periodName,
        );
        useDispatcher(
            this,
            reload({
                user: this._authService.user,
                periodTypes: [type.periodTypeId],
            }),
        );
    }

    /** @private */
    _onTrendAction(): void {
        addPageAction(actions.MyScorecardCard.TREND_ACTION);
        this._onExpandClick(true);
    }

    /** @private */
    _onSelectAction(item: MenuItems): void {
        const menuItem = MenuItems[item];
        addPageAction(actions.MyScorecardCard.SELECT_ACTION, { menuItem });
        this._launchScorecard();
    }

    /** @private */
    _onExpandClick(headerClick?: boolean): void {
        this._isExpanded = !this._isExpanded;
        addPageAction(actions.MyScorecardCard.EXPAND_CLICK, {
            expanded: this._isExpanded,
        });
        this._trendMode = headerClick;
        const cardElement =
            this._el.nativeElement.querySelector('.my-scorecard');
        cardElement?.parentElement.classList.toggle(
            'insight-component-item--expanded',
        );

        this._navigate(headerClick);

        // scroll
        this._scrollIntoView('.my-scorecard');
    }

    /** @private */
    _launchScorecard(): void {
        void this._router.navigate(
            [
                'scorecards',
                this.state.selectedScorecard.header.userId,
                this.state.selectedScorecard.header.scorecardId,
            ],
            {
                queryParams: {
                    periodTypeId:
                        this.state.selectedScorecard.header.periodType,
                },
            },
        );
    }

    /** @private */
    _navigate(trendMode: boolean): void {
        const queryParams = {
            userId: this.state.selectedScorecard.header.userId,
            scorecardId: this.state.selectedScorecard.header.scorecardId,
            periodTypeId: this.state.selectedScorecard.header.periodType,
            fromInsightCard: true,
            trendMode,
        };

        addPageAction(actions.MyScorecardCard.NAVIGATE, queryParams);

        void this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams,
            queryParamsHandling: 'merge',
        });
    }

    /** @private */
    _onPrevSlide(): void {
        addPageAction(actions.MyScorecardCard.PREV_SCORECARD_CLICK);
        this._onNavigate({ direction: -1 });
    }

    /** @private */
    _onNextSlide(): void {
        addPageAction(actions.MyScorecardCard.NEXT_SCORECARD_CLICK);
        this._onNavigate({ direction: 1 });
    }

    /** @private */
    _onNavigate(historyPosNav: HistoryPositionNavigate): void {
        useDispatcher(this, navigateHistory({ historyPosNav }));
    }

    stateToProps(prevState?: ScorecardState): void {
        this._reloadCheck(prevState);

        if (
            prevState.selectedScorecard?.header.scorecardId != null &&
            prevState.selectedScorecard?.header?.scorecardId !==
                this.state.selectedScorecard?.header?.scorecardId
        ) {
            this._navigate(this._trendMode);
        }
    }

    private _reloadCheck(prevState: ScorecardState): void {
        if (
            prevState.pending.reload === true &&
            this.state.pending.reload === false &&
            this.state.scorecardHistory != null &&
            this.state.selectedPeriodType != null
        ) {
            const loaded = Object.keys(this.state.scorecardDataset);
            const scorecardIds: number[] = [];

            this.state.periodTypes?.forEach((periodType) => {
                const dat =
                    this.state.scorecardHistory[periodType.periodTypeId];
                if (dat == null) {
                    return;
                }
                const ids = dat
                    .map((e) => e.scorecardId)
                    .filter(
                        (e) =>
                            !loaded.includes(
                                datasetKey(periodType.periodTypeId, e),
                            ),
                    );
                scorecardIds.push(...ids);
            });

            useDispatcher(this, listData({ scorecardIds }));
        }
    }

    private _scrollIntoView(selector: string): void {
        this._ngZone.onStable
            .pipe(
                take(1),
                delay(1000),
                tap(() => {
                    const el = this._el.nativeElement.querySelector(
                        selector,
                    ) as HTMLElement;
                    if (el == null) {
                        throw new Error('not found');
                    }
                    this._scrollIn(el);
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        scan((acc) => {
                            if (acc >= 5) {
                                throw new Error('still not found');
                            }
                            return acc + 1;
                        }, 0),
                        auditTime(0, animationFrameScheduler),
                    ),
                ),
                catchError(() => {
                    return of(null);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _scrollIn(element: HTMLElement): void {
        const rect = element.parentElement.getBoundingClientRect();
        const scrollTop =
            window.pageYOffset !== 0
                ? window.pageYOffset
                : document.documentElement.scrollTop;
        const offset = {
            top: rect.top + scrollTop,
        };

        const navbarHeight = document
            .querySelector('cmps-nav>.navbar.fixed-top')
            .getBoundingClientRect().height;
        const top = offset.top - navbarHeight - 17;
        try {
            window.scrollTo({
                left: 0,
                top,
                behavior: 'smooth',
            });
        } catch (e) {
            window.scrollTo(0, top);
        }
    }
}
